/**
 * The bench's adaptive concurrency limiter (BENCH-V3-PLAN §3.3, DESIGN §2.63 ED-86). The customer's rate tier is unknown, so
 * the number of model calls in flight is found at run time:
 *
 * - **AIMD.** Start at `initial` (4); a rate-limit or overload error (429 / 529, `rate_limit_error` / `overloaded_error`)
 *   halves the limit (floor `min`) and holds every NEW launch until the server's `retry-after`, or a jittered backoff
 *   (`backoffMs`, provider-anthropic), has passed; the failed call is retried from the head of the queue, up to `maxRetries`.
 *   `growAfter` (10) consecutive successes add one, up to `max`. Several calls failing in the same congestion event (inside
 *   the same hold) halve once, not once each: a burst of 4 must not collapse the limit to 1.
 * - **Budget cap.** While `now < resetAt`, `noteBudget` caps the limit at `⌊outputTokensRemaining / median output per call⌋`
 *   (at least 1), once 3 outputs are recorded. The running median covers the last {@link MEDIAN_WINDOW} calls.
 * - **FIFO.** Calls start in the order `run` was called (the worker issues them in priority order); a retried call keeps the
 *   head of the queue.
 * - **Cancellation.** An aborted `signal` rejects a call that is queued or waiting to retry with a `cancelled`
 *   {@link ProviderError}; a call already in flight is left to its own signal.
 *
 * Other errors reject at once and change nothing. The limiter holds no keys and logs nothing.
 */
import type { CompletionRequest, CompletionResponse, Provider } from '../../engine/bench/types';
import { ProviderError } from './errors';
import { backoffMs } from './provider-anthropic';

/** Outputs kept for the running median. */
export const MEDIAN_WINDOW = 200;
/** Recorded outputs needed before the budget cap applies. */
export const MEDIAN_MIN_SAMPLES = 3;

export interface LimiterState {
	/** The limit now in force: the AIMD limit, lowered by the budget cap while one applies. */
	limit: number;
	inFlight: number;
	queued: number;
}

export interface LimiterOptions {
	/** Starting limit (default 4). */
	initial?: number;
	/** Ceiling (default 8). */
	max?: number;
	/** Floor (default 1). */
	min?: number;
	/** Consecutive successes that add one to the limit (default 10). */
	growAfter?: number;
	/** Retries of a rate-limited call before its error is rethrown (default 6). */
	maxRetries?: number;
	baseDelayMs?: number;
	maxDelayMs?: number;
	/** Test seams. `sleep` must let `now` advance (the limiter wakes from a hold by sleeping until it ends). */
	now?: () => number;
	sleep?: (ms: number) => Promise<void>;
	random?: () => number;
	/** Fires on every change of `limit`, `inFlight` or `queued`. */
	onChange?: (state: LimiterState) => void;
}

export interface Limiter {
	run<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T>;
	/** A rate limit seen elsewhere (a provider that retries internally): the decrease and the launch hold, no retry. */
	noteRateLimited(retryAfterMs: number | null): void;
	/** The output-token budget from the `anthropic-ratelimit-output-tokens-*` headers; `resetAt` is epoch ms. */
	noteBudget(info: { outputTokensRemaining: number; resetAt: number }): void;
	/** Output tokens of a finished call, for the running median. */
	recordOutput(tokens: number): void;
	state(): LimiterState;
}

interface Waiter {
	fn: () => Promise<unknown>;
	resolve: (v: unknown) => void;
	reject: (e: unknown) => void;
	signal?: AbortSignal;
	onAbort?: () => void;
	/** Rate-limit failures of this call so far. */
	failures: number;
	queued: boolean;
}

const cancelled = () => new ProviderError({ status: 0, type: 'cancelled', message: 'cancelled' });

/** True for the errors that mean "slow down": 429 / 529 or the matching API error types. */
export function isRateLimitError(err: unknown): err is ProviderError {
	return (
		err instanceof ProviderError &&
		(err.status === 429 ||
			err.status === 529 ||
			err.type === 'rate_limit_error' ||
			err.type === 'overloaded_error')
	);
}

const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function median(values: readonly number[]): number {
	const s = [...values].sort((a, b) => a - b);
	const mid = s.length >> 1;
	return s.length % 2 === 1
		? (s[mid] as number)
		: ((s[mid - 1] as number) + (s[mid] as number)) / 2;
}

export function createLimiter(opts: LimiterOptions = {}): Limiter {
	const max = Math.max(1, Math.floor(opts.max ?? 8));
	const min = Math.min(max, Math.max(1, Math.floor(opts.min ?? 1)));
	let limit = Math.min(max, Math.max(min, Math.floor(opts.initial ?? 4)));
	const growAfter = Math.max(1, opts.growAfter ?? 10);
	const maxRetries = opts.maxRetries ?? 6;
	const base = opts.baseDelayMs ?? 1000;
	const maxDelay = opts.maxDelayMs ?? 60_000;
	const now = opts.now ?? Date.now;
	const sleep = opts.sleep ?? defaultSleep;
	const random = opts.random ?? Math.random;

	const queue: Waiter[] = [];
	let inFlight = 0;
	let streak = 0;
	let holdUntil = Number.NEGATIVE_INFINITY;
	let wakePending = false;
	const outputs: number[] = [];
	let budget: { remaining: number; resetAt: number } | null = null;
	let last = '';

	/** The limit in force now: the AIMD limit under the budget cap, if one applies. */
	function effective(): number {
		if (budget && now() < budget.resetAt && outputs.length >= MEDIAN_MIN_SAMPLES) {
			const med = median(outputs);
			if (med > 0) return Math.min(limit, Math.max(1, Math.floor(budget.remaining / med)));
		}
		return limit;
	}

	function state(): LimiterState {
		return { limit: effective(), inFlight, queued: queue.length };
	}

	function emit(): void {
		if (!opts.onChange) return;
		const s = state();
		const key = `${s.limit}/${s.inFlight}/${s.queued}`;
		if (key === last) return;
		last = key;
		opts.onChange(s);
	}

	/** One congestion event: halve once per hold, restart the success streak, and hold new launches. */
	function penalize(retryAfter: number | null, attempt: number): void {
		const t = now();
		if (t >= holdUntil) limit = Math.max(min, Math.floor(limit / 2));
		streak = 0;
		const delay = backoffMs(attempt, retryAfter, { base, max: maxDelay, random });
		holdUntil = Math.max(holdUntil, t + delay);
	}

	function settle(w: Waiter, how: 'resolve' | 'reject', v: unknown): void {
		if (w.onAbort) w.signal?.removeEventListener('abort', w.onAbort);
		w[how](v);
	}

	function start(w: Waiter): void {
		w.queued = false;
		inFlight++;
		let p: Promise<unknown>;
		try {
			p = w.fn();
		} catch (e) {
			p = Promise.reject(e);
		}
		p.then(
			(v) => {
				inFlight--;
				if (++streak >= growAfter) {
					limit = Math.min(max, limit + 1);
					streak = 0;
				}
				settle(w, 'resolve', v);
				pump();
				emit();
			},
			(err: unknown) => {
				inFlight--;
				if (isRateLimitError(err)) {
					w.failures++;
					penalize(err.retryAfterMs, w.failures - 1);
					if (w.failures > maxRetries || w.signal?.aborted) {
						settle(w, 'reject', w.signal?.aborted ? cancelled() : err);
					} else {
						w.queued = true;
						queue.unshift(w);
					}
				} else {
					settle(w, 'reject', err);
				}
				pump();
				emit();
			}
		);
	}

	function pump(): void {
		while (queue.length > 0) {
			const t = now();
			if (t < holdUntil) {
				if (!wakePending) {
					wakePending = true;
					void sleep(holdUntil - t).then(() => {
						wakePending = false;
						pump();
						emit();
					});
				}
				return;
			}
			if (inFlight >= effective()) return;
			start(queue.shift() as Waiter);
		}
	}

	return {
		run<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
			return new Promise<T>((resolve, reject) => {
				if (signal?.aborted) return reject(cancelled());
				const w: Waiter = {
					fn,
					resolve: resolve as (v: unknown) => void,
					reject,
					signal,
					failures: 0,
					queued: true,
				};
				if (signal) {
					w.onAbort = () => {
						if (!w.queued) return;
						const i = queue.indexOf(w);
						if (i >= 0) queue.splice(i, 1);
						w.queued = false;
						settle(w, 'reject', cancelled());
						emit();
					};
					signal.addEventListener('abort', w.onAbort, { once: true });
				}
				queue.push(w);
				pump();
				emit();
			});
		},
		noteRateLimited(retryAfterMs: number | null): void {
			penalize(retryAfterMs, 0);
			emit();
		},
		noteBudget(info: { outputTokensRemaining: number; resetAt: number }): void {
			if (!Number.isFinite(info.outputTokensRemaining) || !Number.isFinite(info.resetAt)) return;
			budget = { remaining: Math.max(0, info.outputTokensRemaining), resetAt: info.resetAt };
			pump();
			emit();
		},
		recordOutput(tokens: number): void {
			if (!Number.isFinite(tokens) || tokens < 0) return;
			outputs.push(tokens);
			if (outputs.length > MEDIAN_WINDOW) outputs.shift();
			emit();
		},
		state,
	};
}

/** A provider whose `complete` goes through the limiter, feeding each answer's output tokens to the running median. */
export function limitProvider(
	provider: Provider,
	limiter: Limiter,
	signal?: AbortSignal
): Provider {
	return {
		async complete(req: CompletionRequest): Promise<CompletionResponse> {
			const res = await limiter.run(() => provider.complete(req), signal);
			limiter.recordOutput(res.usage?.outputTokens ?? Math.ceil(res.text.length / 4));
			return res;
		},
	};
}
